import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Center,
  Checkbox,
  Divider,
  Heading,
  HStack,
  Pressable,
  Spinner,
  Switch,
  Text,
  useDisclose,
  VStack,
} from "native-base";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import classNames from "classnames";
import { BiImageAdd } from "react-icons/bi";
import ImageViewer from "react-simple-image-viewer";
import { MdDeleteForever } from "react-icons/md";
import JoditEditor from "jodit-react";
import styles from "./styles.module.css";
import colors from "../../../../constants/colors";
import routes from "../../../../routes";
import Itinerary from "../../../../components/itinerary";
import { StateContext, StatusContext } from "../../../../contexts";
import API, { getFileUrl } from "../../../../api";
import Cell from "../../../../components/cell";
import { formatToCurrency, getConfigUploader } from "../../../../utils";
import BankAccountModal from "../../../../components/bankAccountModal";
import Image from "../../../../components/image";

function AgencyPaymentManagementPricingPage() {
  const params = useParams();
  const navigate = useNavigate();
  const inputFile = useRef();
  const jodit = useRef();
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const [itinerary, setItinerary] = useState([]);
  const [servicePrices, setServicePrices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPublic, setIsPublic] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [responsible, setResponsible] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [bankAccount, setBankAccount] = useState({});
  const [images, setImages] = useState([]);
  const {
    isOpen: isBankAccountModal,
    onOpen: onOpenBankAccountModal,
    onClose: onCloseBankAccountModal,
  } = useDisclose();
  const [isImageViewer, setIsImageViewer] = useState(false);
  const [imageViewerIndex, setImageViewerIndex] = useState(0);

  const getQuotation = useCallback(async () => {
    try {
      if (!state.jwt) {
        return;
      }
      setIsLoading(true);
      const {
        data: {
          data: {
            itinerary: _itinerary,
            services,
            name: _name,
            description: _description,
            responsible: _responsible,
            paymentTypes: _paymentTypes,
            agency: { bankAccount: _bankAccount },
            isPublic: _isPublic,
            photos: _photos,
          },
        },
      } = await API(state.jwt).get(`/tours/${params.id}?`);
      setBankAccount(_bankAccount || {});
      setName(_name || "");
      setDescription(_description || "");
      setResponsible(
        _responsible || `${state.user.firstName} ${state.user.lastName}`,
      );
      setItinerary(_itinerary);
      setIsPublic(_isPublic);
      setServicePrices(
        services
          .flatMap((service) => service.prices)
          .filter(
            (price, index, prices) =>
              index === prices.findIndex((_price) => price.id === _price.id),
          )
          .map((price) => {
            const _services = services.filter((service) =>
              service.prices.find((_price) => price.id === _price.id),
            );
            const [{ quotation, prices }] = _services;
            return {
              vehicleType: quotation.vehicleType,
              tourismType: quotation.tourismType,
              services: _services,
              quotation,
              options: prices,
            };
          })
          .filter(
            (price, index, prices) =>
              index ===
              prices.findIndex(
                (_price) =>
                  price.vehicleType.id === _price.vehicleType.id &&
                  price.tourismType.id === _price.tourismType.id,
              ),
          ),
      );
      setImages(_photos || []);
      setPaymentTypes(_paymentTypes);
    } catch (error) {
      status.updateErrorMessage(error);
    }
    setIsLoading(false);
  }, [params.id, state.jwt, state.user.firstName, state.user.lastName, status]);

  useEffect(() => {
    getQuotation();
  }, [getQuotation]);

  useEffect(() => {
    if (description && jodit) {
      jodit.current.setEditorValue(description);
    }
  }, [description, jodit]);

  const onAddServicePrice = useCallback(
    (service) => {
      const _servicePrices = [...servicePrices];
      _servicePrices[service].options.push({
        name: "",
        cost: "",
        capacity: "",
      });
      setServicePrices(_servicePrices);
    },
    [servicePrices],
  );
  const onUpdateServicePrice = (service, option, data) => {
    const _servicePrices = [...servicePrices].map((_servicePrice, _index) => {
      if (service === _index) {
        const _options = [..._servicePrice.options];
        _options[option] = {
          ..._options[option],
          ...data,
        };
        return { ..._servicePrice, options: _options };
      }
      return _servicePrice;
    });
    setServicePrices(_servicePrices);
  };

  const isPaymentType = useCallback(
    (paymentType) => paymentTypes.some(({ type }) => type === paymentType),
    [paymentTypes],
  );

  const onSave = async () => {
    try {
      if (
        isPaymentType("cash") &&
        Object.values(bankAccount)
          .map(Boolean)
          .some((value) => !value)
      ) {
        onOpenBankAccountModal();
        return;
      }
      setIsSaving(true);
      if (isPaymentType("cash")) {
        await API(state.jwt).put(`/agencies/${state.user.agency.id}`, {
          data: { bankAccount },
        });
      }
      const photos = await Promise.all(
        images.map(async (image) => {
          if (image.id) {
            return image.id;
          }
          const formData = new FormData();
          formData.append("files", image.file);
          const {
            data: [{ id }],
          } = await API(state.jwt).post("/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          return id;
        }),
      );
      await API(state.jwt).put(`/tours/${params.id}`, {
        data: {
          isPublic,
          name,
          description: jodit.current.getEditorValue() || "",
          responsible,
          paymentTypes,
          photos,
        },
      });
      await Promise.all(
        servicePrices
          .flatMap((servicePrice) =>
            servicePrice.options.map((option) => {
              const _servicePrice = { ...servicePrice, ...option };
              delete _servicePrice.options;
              delete _servicePrice.quotation;
              return _servicePrice;
            }),
          )
          .map((servicePrice) =>
            servicePrice?.id
              ? API(state.jwt).put(`/service-prices/${servicePrice.id}`, {
                  data: servicePrice,
                })
              : API(state.jwt).post("/service-prices", {
                  data: servicePrice,
                }),
          ),
      );
      navigate(routes.agencyPaymentManagement);
      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);
      status.updateErrorMessage(error);
    }
  };

  const total = useMemo(
    () =>
      servicePrices.reduce(
        (total, servicePrice) =>
          total +
          servicePrice.options.reduce(
            (_total, option) => _total + option.cost * option.capacity,
            0,
          ) *
            servicePrice.services.length,
        0,
      ),
    [servicePrices],
  );

  const totalServices = useMemo(
    () =>
      servicePrices.reduce(
        (total, servicePrice) =>
          total + servicePrice.quotation.price * servicePrice.services.length,
        0,
      ),
    [servicePrices],
  );

  const isSeatingRight = useMemo(
    () =>
      servicePrices.every(
        (servicePrice) =>
          servicePrice.vehicleType.capacity ===
          servicePrice.options.reduce(
            (total, option) => total + Number(option.capacity),
            0,
          ),
      ),
    [servicePrices],
  );

  const onTogglePaymentType = useCallback(
    (paymentType) => {
      let _paymentTypes = [...paymentTypes];
      if (isPaymentType(paymentType)) {
        _paymentTypes = _paymentTypes.filter(
          (_paymentType) => _paymentType.type !== paymentType,
        );
      } else {
        _paymentTypes.push({ type: paymentType });
      }
      setPaymentTypes(_paymentTypes);
    },
    [paymentTypes, isPaymentType],
  );

  const onAddImage = useCallback(
    (event) => {
      if (event.target.files.length > 0) {
        const _images = [...images];
        const file = event.target.files[0];
        _images.push({
          url: URL.createObjectURL(file),
          file,
        });
        setImages(_images);
      }
      inputFile.current.value = "";
    },
    [images],
  );

  const onRemoveImage = useCallback(
    (index) => {
      const _images = [...images];
      _images.splice(index, 1);
      setImages(_images);
    },
    [images],
  );

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}>
      <Helmet>
        <title>Selección de vehículo</title>
      </Helmet>
      <HStack space={10}>
        <HStack paddingY={8} space={4}>
          <Heading color={colors.secondary} fontSize="2xl">
            Resumen
            <br />
            de tu viaje
          </Heading>
        </HStack>
        <HStack paddingY={8} space={4}>
          <Heading color={colors.secondary} fontSize="xl">
            Asignación de precio por asiento
          </Heading>
        </HStack>
      </HStack>
      <HStack space={3} alignItems="flex-start">
        <VStack
          backgroundColor={colors.white}
          padding={5}
          maxWidth={300}
          width="full">
          {isLoading ? (
            <Center height={300}>
              <Spinner size="lg" color={colors.primary} />
            </Center>
          ) : (
            <Itinerary itinerary={itinerary} />
          )}
        </VStack>
        {isLoading ? (
          <VStack
            flex={1}
            backgroundColor={colors.white}
            paddingX={10}
            paddingY={8}>
            <Center height={500}>
              <Spinner size="lg" color={colors.primary} />
            </Center>
          </VStack>
        ) : (
          <VStack
            flex={1}
            backgroundColor={colors.white}
            paddingX={10}
            paddingY={8}>
            <Heading color={colors.secondary} fontSize="xl">
              Asignación de
              <br />
              precio por asiento
            </Heading>
            <HStack marginTop={5} space={10} alignItems="flex-end">
              <label className={styles.input}>
                <Text color={colors.secondary}>
                  Ingresa el nombre de tu viaje
                </Text>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className={styles.input}>
                <Text color={colors.secondary}>
                  Ingresa el nombre del responsable o<br />
                  coordinador del viaje
                </Text>
                <input
                  type="text"
                  required
                  value={responsible}
                  onChange={(e) => setResponsible(e.target.value)}
                />
              </label>
            </HStack>
            <VStack marginTop={5}>
              <label className={styles.input}>
                <Text color={colors.secondary} marginBottom="10px">
                  Descripción de tu viaje
                </Text>
                <JoditEditor
                  editorRef={(ref) => (jodit.current = ref)}
                  required
                  config={{
                    language: "es",
                    disabled: isSaving,
                    uploader: getConfigUploader(state.jwt),
                  }}
                />
              </label>
            </VStack>
            <VStack marginTop={5} space={3}>
              <Text color={colors.secondary}>Fotos</Text>
              <HStack overflowX="auto" overflowY="hidden" space={3}>
                {images.map((image, index) => (
                  <Box key={String(index)} position="relative">
                    <button
                      key={String(index)}
                      onClick={() => {
                        setIsImageViewer(true);
                        setImageViewerIndex(index);
                      }}
                      type="button">
                      <Image
                        image={getFileUrl(image.url)}
                        width={120}
                        height={120}
                      />
                    </button>
                    <button
                      className={styles.deleteButton}
                      onClick={(event) => {
                        event.stopPropagation();
                        onRemoveImage(index);
                      }}
                      type="button">
                      <Box
                        backgroundColor="white"
                        shadow={2}
                        padding={1}
                        borderRadius="full">
                        <MdDeleteForever size={20} color={colors.primary} />
                      </Box>
                    </button>
                  </Box>
                ))}
                <button onClick={() => inputFile.current.click()} type="button">
                  <BiImageAdd
                    name="image-plus"
                    size={40}
                    color={colors.primary}
                  />
                </button>
                <input
                  ref={inputFile}
                  className={styles.hidden}
                  type="file"
                  accept="image/*"
                  onChange={onAddImage}
                />
              </HStack>
            </VStack>
            <Divider backgroundColor={colors.primary} marginY={10} />
            <Text bold color={colors.secondary}>
              Forma de pago a recibir por mis pasajeros
            </Text>
            <br />
            <Checkbox
              colorScheme="danger"
              isDisabled={isSaving}
              isChecked={isPaymentType("cash")}
              onChange={() => onTogglePaymentType("cash")}>
              <Text>Efectivo</Text>
            </Checkbox>
            <Pressable marginLeft={28} onPress={onOpenBankAccountModal}>
              <Text underline fontSize="sm">
                Datos de cuenta
              </Text>
            </Pressable>
            <Checkbox
              colorScheme="danger"
              isDisabled={isSaving}
              isChecked={isPaymentType("transfer")}
              onChange={() => onTogglePaymentType("transfer")}>
              <Text>Transferencia / Depósito</Text>
            </Checkbox>
            <Checkbox
              colorScheme="danger"
              isDisabled={isSaving}
              isChecked={isPaymentType("card")}
              onChange={() => onTogglePaymentType("card")}>
              <Text>Tarjeta de crédito / Débito</Text>
            </Checkbox>
            <Divider backgroundColor={colors.primary} marginY={10} />
            <Text bold color={colors.secondary}>
              Asignación de precio por tipo de pasajero
            </Text>
            <br />
            <br />
            <table>
              <thead>
                <tr>
                  <Cell header>Tipo de pasajero</Cell>
                  <Cell header>Asigna precio</Cell>
                  <Cell header>
                    <Text bold>
                      Estimación de asientos a<br />
                      comercializar por unidad
                    </Text>
                  </Cell>
                </tr>
              </thead>
              <tbody>
                {servicePrices.map((servicePrice, index) => (
                  <Fragment key={String(index)}>
                    <tr>
                      <Cell> </Cell>
                    </tr>
                    <tr>
                      <Cell>
                        {servicePrice.vehicleType.name} (
                        {servicePrice.vehicleType.capacity} asientos) x
                        {servicePrice.services.length} unidad
                        {servicePrice.services.length !== 1 && "es"}
                      </Cell>
                    </tr>
                    {servicePrice.options.map((option, _index) => (
                      <tr key={String(_index)}>
                        <Cell>
                          <label
                            className={classNames(
                              styles.input,
                              styles.inputForm,
                            )}>
                            <input
                              required
                              placeholder="Nombre"
                              value={option.name}
                              onChange={(e) =>
                                onUpdateServicePrice(index, _index, {
                                  name: e.target.value,
                                })
                              }
                              disabled={isSaving}
                            />
                          </label>
                        </Cell>
                        <Cell>
                          <label
                            className={classNames(
                              styles.input,
                              styles.inputForm,
                            )}>
                            <input
                              required
                              placeholder="Precio"
                              type="number"
                              value={option.cost}
                              onChange={(e) =>
                                onUpdateServicePrice(index, _index, {
                                  cost: e.target.value,
                                })
                              }
                              min={0}
                              disabled={isSaving}
                            />
                          </label>
                        </Cell>
                        <Cell>
                          <label
                            className={classNames(
                              styles.input,
                              styles.inputForm,
                              styles.inputFormNumber,
                            )}>
                            <input
                              required
                              placeholder="Valor"
                              type="number"
                              value={option.capacity}
                              onChange={(e) =>
                                onUpdateServicePrice(index, _index, {
                                  capacity: e.target.value,
                                })
                              }
                              min={0}
                              max={servicePrice.vehicleType.capacity}
                              disabled={isSaving}
                            />
                          </label>
                        </Cell>
                      </tr>
                    ))}
                    <tr>
                      <Cell> </Cell>
                    </tr>
                    <tr>
                      <Cell>
                        <Pressable
                          onPress={() => onAddServicePrice(index)}
                          maxWidth="120px"
                          disabled={isSaving}>
                          <HStack
                            backgroundColor={colors.primary}
                            paddingY={1}
                            borderRadius="full"
                            justifyContent="center">
                            <Text color={colors.white}>Añadir</Text>
                          </HStack>
                        </Pressable>
                      </Cell>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
            <br />
            <Divider backgroundColor={colors.primary} marginY={10} />
            <Text bold color={colors.secondary}>
              Con los precios asignados tu recibes
            </Text>
            <br />
            <table className={styles.table}>
              <thead>
                <tr>
                  <Cell header>Tipo de pasajero</Cell>
                  <Cell header>Precio</Cell>
                  <Cell header>
                    <Text bold>Asientos </Text>
                  </Cell>
                  <Cell header>Ganancia</Cell>
                </tr>
              </thead>
              <tbody>
                {servicePrices.map((servicePrice, index) => (
                  <Fragment key={String(index)}>
                    <tr>
                      <Cell> </Cell>
                    </tr>
                    <tr>
                      <Cell>
                        {servicePrice.vehicleType.name} (
                        {servicePrice.vehicleType.capacity} asientos) x
                        {servicePrice.services.length} unidad
                        {servicePrice.services.length !== 1 && "es"}
                      </Cell>
                    </tr>
                    <tr>
                      <Cell> </Cell>
                    </tr>
                    {servicePrice.options
                      .filter(
                        (option) =>
                          Boolean(option.name) &&
                          Boolean(option.cost) &&
                          Boolean(option.capacity),
                      )
                      .map((option, _index) => (
                        <tr key={String(_index)}>
                          <Cell bold color={colors.secondary} paddingX={5}>
                            {option.name}
                          </Cell>
                          <Cell paddingX={5}>
                            {formatToCurrency(option.cost)}
                          </Cell>
                          <Cell paddingX={5}>
                            {String(
                              option.capacity * servicePrice.services.length,
                            )}
                          </Cell>
                          <Cell bold paddingX={5}>
                            {formatToCurrency(
                              Number(option.cost) *
                                Number(option.capacity) *
                                servicePrice.services.length,
                            )}
                          </Cell>
                        </tr>
                      ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
            <Divider backgroundColor={colors.primary} marginY={10} />
            <Text>
              <Text bold color={colors.secondary}>
                Importe total
              </Text>
              <Text> {formatToCurrency(total)}</Text>
            </Text>
            <Text>
              <Text bold color={colors.secondary}>
                Total a Pagar a Autotur
              </Text>
              <Text> {formatToCurrency(totalServices)}</Text>
            </Text>
            <br />
            <Text underline color={colors.secondary}>
              <Text bold>Ganancia </Text>
              <Text color="dark.50">
                {formatToCurrency(total - totalServices)}
              </Text>
            </Text>
            <Heading color={colors.secondary} fontSize="xl" marginTop={10}>
              ¿Cómo quieres comercializar tu viaje ?
            </Heading>
            <HStack space={1} marginY={4}>
              <Switch
                defaultIsChecked={false}
                size="sm"
                onTrackColor={colors.secondary}
                offTrackColor={colors.lightGray}
                value={isPublic}
                onValueChange={setIsPublic}
                isDisabled={isSaving}
                _hover={{
                  onTrackColor: colors.secondary,
                  offTrackColor: colors.lightGray,
                }}
              />
              <Text>Este viaje es público</Text>
            </HStack>
            <Text fontSize="sm">
              {isPublic
                ? "Su viaje se podrá buscar y reservar públicamente en la aplicación movil de Autotur y en la pagina web www.autotur.com.mx, desliza para que tu viaje sea privado."
                : "Este viaje es privado. Su viaje se podrá buscar y reservar mediante un link qué podrá compartir con sus contactos."}
            </Text>
            <Divider backgroundColor={colors.primary} marginY={10} />
            <VStack alignItems="flex-end" space={2}>
              <button disabled={!isSeatingRight || isSaving}>
                <Box
                  backgroundColor={colors.primary}
                  paddingY={3}
                  paddingX={100}
                  borderRadius="full">
                  <Text color={colors.white}>Continuar</Text>
                </Box>
              </button>
              {!isSeatingRight && (
                <Text color="red.500">
                  La estimación de asientos debe coincidir con el <br />
                  número de asientos disponibles para cada unidad
                </Text>
              )}
            </VStack>
          </VStack>
        )}
      </HStack>
      <BankAccountModal
        isOpen={isBankAccountModal}
        onRequestClose={onCloseBankAccountModal}
        bankAccount={bankAccount}
        setBankAccount={setBankAccount}
      />
      {isImageViewer && (
        <ImageViewer
          src={images.map((image) => getFileUrl(image.url))}
          backgroundStyle={{ backgroundColor: "rgba(0,0,0,0.8)", zIndex: 10 }}
          currentIndex={imageViewerIndex}
          closeOnClickOutside
          onClose={() => setIsImageViewer(false)}
        />
      )}
    </form>
  );
}

export default AgencyPaymentManagementPricingPage;
